import {
  getWordReadingFluencyLevel,
  type ResponseId,
  WordReadingFluencyEnum,
} from "@bindery/shared/contracts/fluency";
import * as z from "zod";
import { storageKey } from "@/site";

export const MILESTONE_THRESHOLDS = [200, 500] as const;
export type Milestone = (typeof MILESTONE_THRESHOLDS)[number];

export const MILESTONE_TOAST_MS = 8_000;

const milestoneSchema = z.literal(MILESTONE_THRESHOLDS);
const milestoneStateSchema = z.object({
  reached: z.array(milestoneSchema),
  pending: z
    .object({ threshold: milestoneSchema, words: z.number().int() })
    .nullable(),
});

/** Per reader, so a fresh reader on the device never inherits a toast. */
export type MilestoneState = z.infer<typeof milestoneStateSchema>;

const EMPTY: MilestoneState = { reached: [], pending: null };

export const milestoneKey = (readerId: string): string =>
  storageKey(`milestoneToast.v1:${readerId}`);

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

export function readMilestones(
  readerId: string,
  storage: Store | null = browserStorage(),
): MilestoneState {
  try {
    const raw = storage?.getItem(milestoneKey(readerId));
    if (raw == null) return EMPTY;
    const parsed = milestoneStateSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

export function writeMilestones(
  readerId: string,
  state: MilestoneState,
  storage: Store | null = browserStorage(),
): void {
  try {
    storage?.setItem(milestoneKey(readerId), JSON.stringify(state));
  } catch {
    return;
  }
}

export function forgetMilestones(
  readerId: string,
  storage: Store | null = browserStorage(),
): void {
  try {
    storage?.removeItem(milestoneKey(readerId));
  } catch {
    return;
  }
}

/**
 * ../app counted every word with any response (its fluency cache's size);
 * "words mastered" counts only the words the fluency model calls Strong.
 */
export function countStrongWords(
  histories: ReadonlyMap<string, readonly ResponseId[]>,
): number {
  let strong = 0;
  for (const history of histories.values()) {
    if (
      getWordReadingFluencyLevel([...history]) === WordReadingFluencyEnum.Strong
    )
      strong += 1;
  }
  return strong;
}

/**
 * Records the thresholds a rise from `before` to `after` strong words
 * crossed. Each threshold is reached once per reader; crossing several at
 * once leaves the highest pending, and a later rise while one is pending
 * only refreshes its count.
 */
export function noteStrongWords(
  state: MilestoneState,
  before: number,
  after: number,
): MilestoneState {
  const crossed = MILESTONE_THRESHOLDS.filter(
    (threshold) =>
      before < threshold &&
      after >= threshold &&
      !state.reached.includes(threshold),
  );
  const highest = crossed.at(-1);
  if (highest !== undefined) {
    return {
      reached: [...state.reached, ...crossed],
      pending: { threshold: highest, words: after },
    };
  }
  if (state.pending !== null && after > state.pending.words) {
    return { ...state, pending: { ...state.pending, words: after } };
  }
  return state;
}

export function withoutPending(state: MilestoneState): MilestoneState {
  return { ...state, pending: null };
}

// ../app's copy, unchanged (use-return-milestone-toast.ts:28-31).
export function milestoneMessage(threshold: Milestone, words: number): string {
  return threshold === 200
    ? `${words} words mastered! Sign in to save your progress.`
    : `Amazing! ${words} words mastered. Your hard work deserves to be saved!`;
}
